/** Display string for v-model value (number) — commas + 2 decimals */
function displayFromBindingValue(raw) {
  if (raw === null || raw === undefined || raw === '') return ''
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(/,/g, ''))
  if (!Number.isFinite(n)) return ''
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

/** v-show etc. use boolean `value`; only treat number/string/null as the v-model. */
function isPlausibleCurrencyModelValue(v) {
  if (v === null) return true
  const t = typeof v
  if (t === 'number') return Number.isFinite(v)
  if (t === 'string') return true
  return false
}

/**
 * v-currency has no arg so binding.value is undefined. Native inputs also drop modelValue from vnode.props
 * (compiler strips it; v-model is a sibling directive). Read the model from vnode.dirs or the DOM.
 */
function resolveModelValue(currencyBinding, vnode, el) {
  if (currencyBinding.value !== undefined) return currencyBinding.value
  const p = vnode?.props
  if (p && Object.prototype.hasOwnProperty.call(p, 'value')) return p.value
  if (p && Object.prototype.hasOwnProperty.call(p, 'modelValue')) return p.modelValue
  const dirs = vnode?.dirs
  if (dirs) {
    for (let i = 0; i < dirs.length; i++) {
      const d = dirs[i]
      if (d.dir === currencyBinding.dir) continue
      if (d.value === undefined) continue
      if (!isPlausibleCurrencyModelValue(d.value)) continue
      return d.value
    }
  }
  if (el) {
    const parsed = parseFloat(String(el.value ?? '').replace(/,/g, ''))
    if (Number.isFinite(parsed)) return parsed
  }
  return undefined
}

/** Add commas to integer part only, preserve decimal as-is */
function addCommas(str) {
  const parts = str.split('.')
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  return parts.join('.')
}

export default {
  beforeMount(el, binding) {

    const onInput = () => {
      el._userTyping = true
      const cursorPos = el.selectionStart
      const oldLen = el.value.length

      // Strip everything except digits, minus, and decimal
      let raw = el.value.replace(/,/g, '')
      raw = raw.replace(/[^0-9.-]/g, '')

      // Only allow one minus at the start
      if (raw.indexOf('-') > 0) raw = raw.replace(/-/g, '')
      // Only allow one decimal point
      const dotIdx = raw.indexOf('.')
      if (dotIdx !== -1) {
        raw = raw.slice(0, dotIdx + 1) + raw.slice(dotIdx + 1).replace(/\./g, '')
        // Limit to 2 decimal places while typing
        const decimals = raw.slice(dotIdx + 1)
        if (decimals.length > 2) raw = raw.slice(0, dotIdx + 3)
      }

      if (raw === '' || raw === '-') {
        el.value = raw
        binding.instance[binding.expression] = null
        return
      }

      // Min/max enforcement
      const min = el.min ? parseFloat(el.min) : null
      const max = el.max ? parseFloat(el.max) : null
      const parsedValue = parseFloat(raw)

      if (max !== null && !isNaN(parsedValue) && parsedValue > max) {
        const capped = addCommas(max.toFixed(2))
        el.value = capped
        binding.instance[binding.expression] = max
        el.setSelectionRange(capped.length, capped.length)
        return
      }

      if (min !== null && !isNaN(parsedValue) && parsedValue < min) {
        const capped = addCommas(min.toFixed(2))
        el.value = capped
        binding.instance[binding.expression] = min
        el.setSelectionRange(capped.length, capped.length)
        return
      }

      // Add commas to integer part, keep decimal as typed
      const formatted = addCommas(raw)
      el.value = formatted

      // Adjust cursor for added/removed commas
      const lenDiff = formatted.length - oldLen
      const newPos = Math.max(0, cursorPos + lenDiff)
      el.setSelectionRange(newPos, newPos)

      // Update the v-model with parsed number
      if (!isNaN(parsedValue) && Number.isFinite(parsedValue)) {
        binding.instance[binding.expression] = parsedValue
      }

      setTimeout(() => { el._userTyping = false }, 100)
    }

    const onKeyPress = (e) => {
      // Block comma (formatting is automatic)
      if (e.key === ',') {
        e.preventDefault()
        return
      }

      // Block minus if min >= 0
      const min = el.min ? parseFloat(el.min) : null
      if (e.key === '-' && min !== null && min >= 0) {
        e.preventDefault()
      }
    }

    const onBlur = () => {
      el._userTyping = false
      const raw = el.value.replace(/,/g, '')
      if (raw === '' || raw === '-') {
        el.value = ''
        binding.instance[binding.expression] = null
        return
      }
      const parsed = parseFloat(raw)
      if (isNaN(parsed)) {
        el.value = ''
        binding.instance[binding.expression] = null
        return
      }
      // Full format on blur: commas + 2 decimals
      el.value = parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      binding.instance[binding.expression] = parsed
    }

    el.addEventListener('input', onInput)
    el.addEventListener('keypress', onKeyPress)
    el.addEventListener('blur', onBlur)
    el._currencyOnInput = onInput
    el._currencyOnKeyPress = onKeyPress
    el._currencyOnBlur = onBlur
  },

  mounted(el, binding, vnode) {
    const v = resolveModelValue(binding, vnode, el)
    if (v === undefined || v === null || v === '') return
    const next = displayFromBindingValue(v)
    if (el.value !== next) el.value = next
  },

  updated(el, binding, vnode) {
    if (el._userTyping || document.activeElement === el) return

    const v = resolveModelValue(binding, vnode, el)
    // If Vue did not supply a model this pass, do not clear.
    if (v === undefined) return

    if (v === null || v === '') {
      if (el.value !== '') el.value = ''
      return
    }

    const next = displayFromBindingValue(v)
    if (el.value !== next) el.value = next
  },

  beforeUnmount(el) {
    if (el._currencyObserver) {
      el._currencyObserver.disconnect()
    }
    if (el._currencyOnInput) {
      el.removeEventListener('input', el._currencyOnInput)
    }
    if (el._currencyOnKeyPress) {
      el.removeEventListener('keypress', el._currencyOnKeyPress)
    }
    if (el._currencyOnBlur) {
      el.removeEventListener('blur', el._currencyOnBlur)
    }
  }
}
